import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { BlueprintVisibility } from 'types/rest/assetCatalogTypes';
import { BlueprintDraft } from '../../../store/blueprints/blueprintsTypes';
import { ApplicationState } from '../../../store/rootReducer';
import ReactSelector from '../../../components/selectors/ReactSelector';

interface Props {
  draft: BlueprintDraft;
  onChange: (patch: Partial<BlueprintDraft>) => void;
  canPublishAccount: boolean;
  /** When true, lay the fields out in two columns to use the full width. */
  twoColumn?: boolean;
  /** When false, hides the visibility selector (e.g. edit view where backend ignores scope). */
  showVisibility?: boolean;
}

const VISIBILITIES: BlueprintVisibility[] = ['PRIVATE', 'ACCOUNT'];

const BlueprintMetaFields = ({ draft, onChange, canPublishAccount, twoColumn, showVisibility = true }: Props) => {
  const { t } = useTranslation('blueprints');
  const allBlueprints = useSelector((s: ApplicationState) => s.blueprints.blueprints);

  const tagOptions = useMemo(() => {
    const seen = new Set<string>();
    allBlueprints
      .flatMap(bp => bp.tags)
      .forEach(tag => seen.add(tag));
    return Array.from(seen).map(tag => ({ label: tag, value: tag, key: tag }));
  }, [allBlueprints]);

  const visLabel: Record<BlueprintVisibility, string> = {
    PRIVATE: t('visibility.private'),
    WORKSPACE: t('visibility.workspace'),
    ACCOUNT: t('visibility.account'),
  };
  const visDesc: Record<BlueprintVisibility, string> = {
    PRIVATE: t('visibility.privateDesc'),
    WORKSPACE: t('visibility.workspaceDesc'),
    ACCOUNT: t('visibility.accountDesc'),
  };

  const nameField = (
    <>
      <label className="small font-weight-bold">{t('form.name')}</label>
      <input
        data-test="bp-name"
        className="form-control mb-3"
        value={draft.name}
        onChange={e => onChange({ name: e.target.value })}
      />
    </>
  );

  const tagsField = (
    <>
      <label className="small font-weight-bold">{t('form.tagsLabel')}</label>
      <div className="mb-3">
        <ReactSelector
          options={tagOptions}
          preSelected={draft.tags.map(tag => ({ label: tag, value: tag, key: tag }))}
          onUpdateTags={(selected: Array<{ label: string }> | null) =>
            onChange({ tags: (selected ?? []).map(item => item.label) })
          }
        />
      </div>
    </>
  );

  const thumbnailField = (
    <>
      <label className="small font-weight-bold">{t('form.thumbnail')}</label>
      <input
        type="file"
        accept="image/*"
        className="form-control-file mb-1"
        onChange={e => {
          const file = e.target.files && e.target.files[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = () => onChange({ thumbnail: String(reader.result) });
          reader.readAsDataURL(file);
        }}
      />
      <div className="text-muted small mb-3">{t('form.thumbnailHint')}</div>
    </>
  );

  const descriptionField = (
    <>
      <label className="small font-weight-bold">{t('form.description')}</label>
      <textarea
        className="form-control mb-3"
        rows={3}
        value={draft.description}
        onChange={e => onChange({ description: e.target.value })}
      />
    </>
  );

  const visibilityField = (
    <>
      <label className="small font-weight-bold">{t('form.visibility')}</label>
      {VISIBILITIES.map(vis => {
        const disabled = vis === 'ACCOUNT' && !canPublishAccount;
        return (
          <div key={vis} className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="bp-visibility"
              data-test={`bp-vis-${vis}`}
              checked={draft.visibility === vis}
              disabled={disabled}
              onChange={() => onChange({ visibility: vis })}
            />
            <label className="form-check-label">
              {visLabel[vis]} <span className="text-muted small">— {visDesc[vis]}</span>
              {disabled && <span className="text-warning small"> ({t('visibility.accountHint')})</span>}
            </label>
          </div>
        );
      })}
    </>
  );

  if (twoColumn) {
    return (
      <div className="d-flex" style={{ gap: 22 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {nameField}
          {tagsField}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {thumbnailField}
          {descriptionField}
          {showVisibility && visibilityField}
        </div>
      </div>
    );
  }

  return (
    <div>
      {thumbnailField}
      {nameField}
      {tagsField}
      {descriptionField}
      {showVisibility && visibilityField}
    </div>
  );
};

export default BlueprintMetaFields;
